import { Globe2, Compass, Target, Eye, HeartHandshake } from "lucide-react";
import SEO from "../lib/seo";
import PageHero from "../components/PageHero";
import SectionHeading from "../components/SectionHeading";
import CtaSection from "../components/CtaSection";
import { Reveal } from "../components/Reveal";
import { useSettings } from "../lib/store";
import { waLink, MSG } from "../lib/whatsapp";

const HERO_IMG =
  "https://images.unsplash.com/photo-1787948258473-0d6ab9a58360?crop=entropy&cs=srgb&fm=jpg&q=80&auto=format&fit=crop&w=1800";

export default function About() {
  const { settings } = useSettings();

  return (
    <>
      <SEO
        title="Tentang Kami — Beyond Travel | Travel More, Explore More"
        description="Beyond Travel adalah perusahaan jasa perjalanan wisata internasional dan domestik dari Indonesia, fokus pada Land Arrangement internasional."
      />
      <PageHero
        image={HERO_IMG}
        eyebrow="About Us"
        title="Beyond Travel"
        sub="Travel More, Explore More — partner perjalanan internasional Anda dari Indonesia."
        crumb="About Us"
      />

      <section className="section bg-white">
        <div className="container-x grid gap-12 lg:grid-cols-2">
          <div>
            <SectionHeading eyebrow="About Beyond Travel" title="Partner Perjalanan Internasional Anda" />
            <Reveal delay={0.15}>
              <p className="mt-5 text-sm leading-relaxed text-slate-600 sm:text-base">
                Beyond Travel adalah perusahaan jasa perjalanan wisata internasional dan domestik dari Indonesia. Kami berfokus pada penjualan Land Arrangement internasional — paket perjalanan di negara tujuan yang mencakup pengaturan itinerary, hotel, transportasi lokal, guide, tiket objek wisata, makan, airport transfer, dan layanan perjalanan lainnya sesuai paket.
              </p>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-4 text-sm leading-relaxed text-slate-600 sm:text-base">
                Kami percaya perjalanan yang baik dimulai dari perencanaan yang baik. Karena itu setiap permintaan kami tanggapi secara personal: dari konsultasi awal, penyusunan proposal, hingga pendampingan selama perjalanan berlangsung.
              </p>
            </Reveal>
          </div>
          <div className="space-y-5">
            <Reveal>
              <div className="flex gap-4 rounded-3xl bg-tosca-600 p-6 text-white shadow-[0_16px_40px_-12px_rgba(15,118,110,0.45)]" data-testid="about-vision">
                <Eye className="h-7 w-7 flex-shrink-0 text-gold" />
                <div>
                  <h3 className="font-heading text-lg font-bold">Vision</h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-white/80">
                    Menjadi partner perjalanan internasional yang terpercaya bagi traveler Indonesia — dikenal karena layanan yang profesional, transparan, dan penuh kepedulian.
                  </p>
                </div>
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="rounded-3xl border border-slate-100 bg-cream p-6" data-testid="about-mission">
                <div className="flex items-center gap-3">
                  <Target className="h-6 w-6 text-tosca-600" />
                  <h3 className="font-heading text-lg font-bold text-charcoal">Mission</h3>
                </div>
                <ul className="mt-4 space-y-2.5">
                  {[
                    "Menyediakan Land Arrangement yang transparan dan mudah dipahami.",
                    "Menyusun itinerary yang sesuai kebutuhan setiap tipe traveler.",
                    "Mendampingi pelanggan dari perencanaan hingga kembali ke rumah.",
                    "Mendukung perjalanan halal dan muslim friendly di seluruh destinasi.",
                  ].map((m) => (
                    <li key={m} className="flex items-start gap-2 text-sm text-slate-500">
                      <HeartHandshake className="mt-0.5 h-4 w-4 flex-shrink-0 text-gold" /> {m}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="section bg-cream">
        <div className="container-x">
          <SectionHeading eyebrow="Our Services" title="Apa yang Kami Kerjakan" align="center" />
          <div className="mt-8 flex flex-wrap justify-center gap-2.5">
            {["Land Arrangement", "Private Trip", "Open Trip", "Corporate Travel", "Group Tour", "Family Trip", "Honeymoon", "Study Tour", "Halal Tour", "Airport Transfer", "Bus Rental", "Hotel Booking"].map((s, i) => (
              <Reveal key={s} delay={Math.min(i * 0.04, 0.3)}>
                <span className="chip border border-slate-200 bg-white px-5 py-2.5 text-sm font-semibold text-charcoal shadow-crystal">
                  <Globe2 className="h-4 w-4 text-tosca-600" /> {s}
                </span>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="section bg-white">
        <div className="container-x">
          <SectionHeading eyebrow="Company Information" title="Informasi Perusahaan" />
          <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {[
              { label: "Website", value: "beyondtravel.co.id", href: "https://beyondtravel.co.id" },
              { label: "Instagram", value: "@beyondtravel.co.id", href: "https://instagram.com/beyondtravel.co.id" },
              { label: "TikTok", value: "@BeyondTourTravel", href: "https://tiktok.com/@BeyondTourTravel" },
              { label: "WhatsApp", value: settings?.whatsapp_display || "+62 881-2551-877", href: waLink(settings, MSG.general) },
              { label: "Email", value: "info@beyondtravel.co.id", href: "mailto:info@beyondtravel.co.id" },
              { label: "Alamat Kantor", value: "Ruko U-Ville Avenue, Jl. Boulevard UPJ Ruko U-Ville A No.5, Sawah Baru, Ciputat, Tangerang Selatan, Banten 15413" },
            ].map((c, i) => (
              <Reveal key={c.label} delay={Math.min(i * 0.06, 0.3)} className="h-full">
                <div className="h-full rounded-2xl border border-slate-100 bg-cream p-5">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">{c.label}</p>
                  {c.href ? (
                    <a href={c.href} target="_blank" rel="noreferrer" className="mt-1.5 block text-sm font-semibold text-tosca-700 transition hover:text-gold-dark" data-testid={`about-contact-${c.label.toLowerCase().replace(/ /g, "-")}`}>
                      {c.value}
                    </a>
                  ) : (
                    <p className="mt-1.5 text-sm font-semibold text-charcoal">{c.value}</p>
                  )}
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CtaSection title="Kenalan Lebih Dekat?" sub="Konsultasikan rencana perjalanan Anda dengan tim Beyond Travel." testid="cta-about" />
    </>
  );
}
